import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import {
  t, Stagger, PageHead, PageTitle, PageSub, StatGrid, Tile,
  ChipBar, Chip, IconBtn, Loading, Empty, Cluster, useUI,
} from './ui'

const LABELS = ['', '😞 Décevant', '😕 Passable', '😐 Correct', '😊 Bien', '🤩 Excellent !']
const TONES = ['', 'red', 'amber', 'gold', 'teal', 'green']

const FILTERS = [
  { key: 'tous', label: 'Tous' },
  { key: 'mobile', label: '📱 Mobile' },
  { key: 'desktop', label: '🖥️ Desktop' },
  { key: '5', label: '🤩 5' },
  { key: '4', label: '😊 4' },
  { key: '3', label: '😐 3' },
  { key: '2', label: '😕 2' },
  { key: '1', label: '😞 1' },
]

const Item = styled.div`
  display: flex; gap: 14px; align-items: flex-start;
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: ${t.r};
  padding: 15px 17px;
  margin-bottom: 10px;
  box-shadow: ${t.shadow};
`
const Face = styled.div`font-size: 1.7rem; line-height: 1; margin-top: 2px; flex-shrink: 0;`
const Head = styled.div`
  display: flex; align-items: center; gap: 9px; flex-wrap: wrap; margin-bottom: 5px;
`
const Stars = styled.span`
  font-family: ${t.font}; font-size: 0.86rem; font-weight: 700; color: ${t.text};
`
const Meta = styled.span`font-family: ${t.font}; font-size: 0.72rem; color: ${t.textFaint};`
const Comment = styled.blockquote`
  font-family: ${t.font}; font-size: 0.84rem; color: ${t.text};
  background: ${t.surfaceAlt}; border-left: 2px solid ${t.line};
  border-radius: 0 ${t.rXs} ${t.rXs} 0;
  padding: 9px 13px; margin: 0 0 6px; line-height: 1.6;
`

export default function FeedbackAdmin() {
  const { confirm, toast } = useUI()
  const [feedbacks, setFeedbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('tous')

  useEffect(() => {
    supabase.from('site_feedback').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { setFeedbacks(data || []); setLoading(false) })
  }, [])

  const deleteFeedback = async (f) => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer cet avis ?',
      body: 'Ce retour visiteur sera définitivement effacé.',
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('site_feedback').delete().eq('id', f.id)
    setFeedbacks((prev) => prev.filter((x) => x.id !== f.id))
    toast('Avis supprimé')
  }

  const filtered = filter === 'tous' ? feedbacks
    : filter === 'mobile' ? feedbacks.filter((f) => f.is_mobile)
    : filter === 'desktop' ? feedbacks.filter((f) => !f.is_mobile)
    : feedbacks.filter((f) => f.rating === parseInt(filter))

  const avg = feedbacks.length ? (feedbacks.reduce((s, f) => s + f.rating, 0) / feedbacks.length).toFixed(1) : '—'

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Feedback site</PageTitle>
          <PageSub>Retours laissés par les visiteurs depuis le site public</PageSub>
        </div>
      </PageHead>

      <StatGrid style={{ marginBottom: 20 }}>
        <Tile i={0} label="Total avis" value={feedbacks.length} tone="neutral" />
        <Tile i={1} label="Note moyenne" value={avg === '—' ? '—' : `${avg} / 5`} tone="gold" />
        <Tile i={2} label="Depuis mobile" value={feedbacks.filter((f) => f.is_mobile).length} tone="blue" />
        <Tile i={3} label="Depuis desktop" value={feedbacks.filter((f) => !f.is_mobile).length} tone="green" />
      </StatGrid>

      <ChipBar $scroll style={{ marginBottom: 18 }}>
        {FILTERS.map((f) => (
          <Chip key={f.key} $on={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}</Chip>
        ))}
      </ChipBar>

      {loading ? (
        <Loading rows={3} h="84px" />
      ) : filtered.length === 0 ? (
        <Empty icon="💬" title="Aucun avis pour ce filtre">
          Les retours laissés depuis le site apparaîtront ici.
        </Empty>
      ) : (
        filtered.map((f, i) => (
          <Stagger key={f.id} $i={i}>
            <Item>
              <Face>{LABELS[f.rating]?.split(' ')[0]}</Face>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Head>
                  <Stars>{'⭐'.repeat(f.rating)} {LABELS[f.rating]?.split(' ').slice(1).join(' ')}</Stars>
                  <Meta>{f.is_mobile ? '📱 Mobile' : '🖥️ Desktop'} · {f.page}</Meta>
                </Head>
                {f.comment && <Comment>« {f.comment} »</Comment>}
                <Meta>
                  {new Date(f.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Meta>
              </div>
              <Cluster>
                <IconBtn $sm onClick={() => deleteFeedback(f)} title="Supprimer">🗑</IconBtn>
              </Cluster>
            </Item>
          </Stagger>
        ))
      )}
    </>
  )
}

export { TONES }
